import {
  useState,
  MouseEvent,
  useRef,
  useCallback,
  RefObject,
  useEffect,
} from "react";

import { validateNumberInput } from "../utils";
import { currencyMultiplier, formatNumber } from "../utils/currencyUtils";

export type Separators = "." | "," | " " | "";

export interface IInputNumber {
  decimalLength?: number;
  decimalSeparator?: Separators;
  disableAbbreviation?: boolean;
  disableMacros?: boolean;
  thousandSeparator?: Separators;
  trimDecimals?: boolean;
  onSave?: (e: Event) => void;
  value?: string | number;
  step?: number;
  limit?: number;
  onEnterKeyPress?: (val: string) => void;
  onChange?: (val: string) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  allowEmptyValue?: boolean;
  onCancel?: (e: Event) => void;
  onBlur?: (e: Event) => void;
  validation?: (
    value: string,
    disableMacros: boolean,
    decimalSeparator: string,
    decimalLength: number,
    limit?: number
  ) => boolean;
  min?: string | number;
  max?: string | number;
  update?: boolean;
}

export const useInputNumber = (
  {
    decimalSeparator = ".",
    thousandSeparator = ",",
    disableAbbreviation,
    disableMacros,
    decimalLength,
    value = "",
    onChange,
    onSave,
    onEnterKeyPress,
    onClick,
    onCancel,
    allowEmptyValue,
    step = 1,
    trimDecimals = true,
    limit,
    validation = validateNumberInput,
    min,
    max,
    onBlur,
    update,
  }: IInputNumber,
  inputRef: RefObject<HTMLInputElement | null>
) => {
  const isMaxValid =
    max === undefined || min === undefined || Number(max) >= Number(min);

  if (!isMaxValid) {
    console.warn(
      "Warning: The maximum value is set to be lower than the minimum value. The maximum value will be ignored."
    );
  }

  const [currentValue, setCurrentValue] = useState(() =>
    formatNumber(
      value as string,
      thousandSeparator,
      decimalSeparator,
      trimDecimals,
      decimalLength,
      disableAbbreviation ? "long" : "short"
    )
  );
  const [prevValue, setPrevValue] = useState(() =>
    formatNumber(
      value as string,
      thousandSeparator,
      decimalSeparator,
      trimDecimals,
      decimalLength,
      disableAbbreviation ? "long" : "short"
    )
  );
  const [unformattedValue, setUnformattedValue] = useState(() =>
    formatNumber(
      value as string,
      "",
      decimalSeparator,
      false,
      decimalLength,
      "long"
    )
  );
  const [unformattedPrevValue, setUnformattedPrevValue] = useState(() =>
    formatNumber(
      value as string,
      "",
      decimalSeparator,
      false,
      decimalLength,
      "long"
    )
  );

  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (value !== prevValue && (!focused || update)) {
      setCurrentValue(
        formatNumber(
          value as string,
          thousandSeparator,
          decimalSeparator,
          trimDecimals,
          decimalLength,
          disableAbbreviation ? "long" : "short"
        )
      );
      setPrevValue(
        formatNumber(
          value as string,
          thousandSeparator,
          decimalSeparator,
          trimDecimals,
          decimalLength,
          disableAbbreviation ? "long" : "short"
        )
      );
      setUnformattedValue(
        formatNumber(
          value as string,
          "",
          decimalSeparator,
          false,
          decimalLength,
          "long"
        )
      );
      setUnformattedPrevValue(
        formatNumber(
          value as string,
          "",
          decimalSeparator,
          false,
          decimalLength,
          "long"
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disableAbbreviation,
    thousandSeparator,
    decimalSeparator,
    decimalLength,
    trimDecimals,
    value,
    focused,
  ]);

  const escapeRef = useRef(false);

  const handleBlur = useCallback(
    (e) => {
      if (escapeRef.current) {
        setCurrentValue(prevValue);
        setUnformattedValue(unformattedPrevValue);
        if (onChange) onChange(String(unformattedPrevValue));
      } else {
        if (e.target.value.trim().length > 0 && prevValue !== e.target.value) {
          const _value = formatNumber(
            currentValue as string,
            thousandSeparator,
            decimalSeparator,
            trimDecimals,
            decimalLength,
            disableAbbreviation ? "long" : "short"
          );
          setPrevValue(_value);
          setUnformattedPrevValue(
            formatNumber(
              currentValue as string,
              "",
              decimalSeparator,
              false,
              decimalLength,
              "long"
            )
          );
          setUnformattedValue(
            formatNumber(
              currentValue as string,
              "",
              decimalSeparator,
              false,
              decimalLength,
              "long"
            )
          );
          setCurrentValue(_value);
          typeof onSave === "function" && onSave(e);
        } else {
          if (!allowEmptyValue) {
            setCurrentValue(prevValue);
            setUnformattedValue(unformattedPrevValue);
            typeof onCancel === "function" && onCancel(e);
          } else {
            if (typeof onSave === "function" && prevValue !== e.target.value) {
              onSave(e);
            } else {
              typeof onCancel === "function" && onCancel(e);
            }
          }
        }
      }
      setFocused(false);
      typeof onBlur === "function" && onBlur(e);
    },
    [
      onBlur,
      prevValue,
      unformattedPrevValue,
      onChange,
      currentValue,
      thousandSeparator,
      decimalSeparator,
      trimDecimals,
      decimalLength,
      disableAbbreviation,
      onSave,
      allowEmptyValue,
      onCancel,
    ]
  );

  const updateValue = useCallback(
    (type: "increment" | "decrement") => {
      const preformattedValue = String(unformattedValue)
        .replace(thousandSeparator, "")
        .replace(decimalSeparator, ".");
      const numericValue = parseFloat(preformattedValue);

      if (isNaN(numericValue)) return;

      if (
        (max !== undefined && numericValue > Number(max)) ||
        (min !== undefined && numericValue < Number(min))
      ) {
        return;
      }

      let newValue = numericValue;

      if (type === "increment") {
        if (max === undefined || numericValue + step <= Number(max)) {
          newValue = numericValue + step;
        } else if (numericValue < Number(max)) {
          newValue = Number(max);
        } else {
          return;
        }
      } else if (type === "decrement") {
        if (min === undefined || numericValue - step >= Number(min)) {
          newValue = numericValue - step;
        } else if (numericValue > Number(min)) {
          newValue = Number(min);
        } else {
          return;
        }
      }

      let formattedValue;
      if (decimalLength !== undefined) {
        formattedValue = newValue.toFixed(decimalLength);
      } else {
        formattedValue = newValue.toFixed(2);
      }

      if (decimalLength !== undefined && decimalLength === 0) {
        formattedValue = Math.round(newValue).toString();
      }

      if (formattedValue.includes(".") || formattedValue.includes(",")) {
        formattedValue = formattedValue.replace(".", decimalSeparator);
      }

      setUnformattedValue(formattedValue);
      setCurrentValue(formattedValue);
      if (onChange) onChange(formattedValue);
    },
    [
      unformattedValue,
      thousandSeparator,
      decimalSeparator,
      decimalLength,
      onChange,
      step,
      min,
      max,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.target.blur();
        if (typeof onEnterKeyPress === "function")
          onEnterKeyPress(e.target.value);
      }
      if (e.key === "ArrowLeft") {
        return;
      }
      if (e.key === "ArrowRight") {
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        updateValue("increment");
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        updateValue("decrement");
      }
      if (e.key === "Escape") {
        escapeRef.current = true;
        e.target.blur();
        typeof onCancel === "function" && onCancel(e);
        escapeRef.current = false;
      }
      if (e.key === "Backspace") {
        return;
      }
      if (e.key === "Delete") {
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "a") {
        inputRef.current?.select();
        return;
      }
      if (e.key === "Tab") {
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "v") {
        e.preventDefault();
        return;
      }

      // Disallow "-" if min is 0 or greater
      if (e.key === "-" && min !== undefined && Number(min) >= 0) {
        e.preventDefault();
        return;
      }

      // Disallow decimal separator if decimalLength is 0
      if (e.key === decimalSeparator && decimalLength === 0) {
        e.preventDefault();
        return;
      }

      const input = e.target;
      const currentValue = input.value;
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const newValue =
        currentValue.substring(0, start) + e.key + currentValue.substring(end);
      if (
        !validation(
          newValue,
          Boolean(disableMacros),
          decimalSeparator,
          decimalLength ?? 0,
          limit
        )
      ) {
        e.preventDefault();
        return;
      }
    },
    [
      decimalLength,
      decimalSeparator,
      disableMacros,
      inputRef,
      limit,
      min,
      onCancel,
      onEnterKeyPress,
      updateValue,
      validation,
    ]
  );

  const handleChange = useCallback(
    (e) => {
      const inputValue = e.target.value;
      const numericInput = disableMacros
        ? inputValue
        : inputValue.replace(
            /(\d+(?:[.,]\d+)?)([kmbtKMBT])/,
            (_, num, unit) => {
              const normalizedNum = num.replace(",", ".");
              const parts = normalizedNum.split(".");
              const integerPart = parts[0];
              const fractionalPart = parts[1];

              const newInteger =
                parseInt(integerPart, 10) *
                currencyMultiplier[unit.toLowerCase()];

              let result = newInteger.toString();
              if (fractionalPart !== undefined) {
                result += decimalSeparator + fractionalPart;
              }
              return result;
            }
          );
      setCurrentValue(numericInput);
      setUnformattedValue(numericInput);
      if (onChange) onChange(numericInput);
    },
    [disableMacros, decimalSeparator, onChange]
  );

  const handleClick = useCallback(
    (e) => {
      if (typeof onClick === "function") {
        onClick(e);
      }
    },
    [onClick]
  );

  const handleFocus = useCallback(() => {
    setCurrentValue(unformattedValue);
    setFocused(true);
  }, [unformattedValue]);

  const handleDoubleClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current?.select();
    }
  }, [inputRef]);

  return {
    value: currentValue,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown,
    onChange: handleChange,
    onClick: handleClick,
    onDoubleClick: handleDoubleClick,
    onFocus: handleFocus,
    focused,
    unformattedValue,
  };
};
